import React        from 'react';
import View          from '@widesk-ui/components/View';
import Alert         from '@widesk-ui/components/Alert';
import Button        from '@widesk-ui/components/Button';
import { Modal }     from '@widesk-ui/components/modal/Modal';
import useTheme      from '@widesk-ui/hooks/useTheme';
import PlanDoubtQueue from '@/TokamapEditor/ux/PlanDoubtQueue';
import { doubtKey }   from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitration }  from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitrations } from '@/TokamapEditor/helpers/planDoubts';
import type { PlanCorrections }  from '@/TokamapEditor/helpers/planDoubts';
import { planShapeState }        from '@/TokamapEditor/helpers/planShapes';
import { PLAN_SHAPE_LEGEND }     from '@/TokamapEditor/helpers/planShapes';
import { PLAN_SHAPE_STYLES }     from '@/TokamapEditor/helpers/planShapes';
import type { PlanShapeState }   from '@/TokamapEditor/helpers/planShapes';
import PlanZoomControls          from '@/TokamapEditor/ux/PlanZoomControls';
import { usePlanViewport }       from '@/TokamapEditor/helpers/usePlanViewport';
import type { PlanView }         from '@/TokamapEditor/helpers/usePlanViewport';
import type { PlanDoubt }        from '@models/map/dxfTypes';
import type { PlanRegion }       from '@models/map/dxfTypes';
import type { PlanShape }        from '@models/map/dxfTypes';
import type { PlanSourceResult } from '@models/map/dxfTypes';

/**
 * **Le tri des formes, sur le plan lui-même** — dans une modale, et pas sur la carte de
 * l'éditeur.
 *
 * ## Pourquoi pas sur la carte
 *
 * L'aperçu vivait jusqu'ici comme des formes d'éditeur posées sur la carte. Trois problèmes,
 * tous constatés à l'usage :
 *
 *   1. **le clic était impossible.** L'éditeur écoute `shape:click` pour sa propre sélection ;
 *      deux gestes concurrents sur le même clic ne se distinguaient pas, d'où le verrouillage
 *      des formes d'aperçu — et donc l'impossibilité de désigner une forme à la souris. Ici la
 *      question ne se pose pas : la modale est seule à recevoir ses clics ;
 *   2. **le plan était posé n'importe où**, au centre de la vue, à une échelle arbitraire : le
 *      placement ne se joue qu'à l'étape suivante. Trier des formes sur un plan mal placé
 *      n'apporte rien que le plan lui-même n'apporterait mieux ;
 *   3. **des centaines de formes d'éditeur** à poser, repeindre et retirer, pour un aperçu
 *      qu'on regarde deux minutes.
 *
 * ## Le repère : les pixels de l'aperçu, tels quels
 *
 * `POST /plan/analysis` rend les contours en PIXELS de l'image de `/plan/source` — le même
 * repère que cette image. Un `<svg viewBox="0 0 width height">` les superpose donc sans la
 * moindre projection : pas de placement, pas d'affine, rien à faire diverger. C'est le seul
 * endroit du parcours où la géométrie ne demande aucun calcul.
 */
type Props = {
	open: boolean;
	source: PlanSourceResult;
	shapes: PlanShape[];
	doubts: PlanDoubt[];
	arbitrations: PlanArbitrations;
	corrections: PlanCorrections;
	/** Vrai quand la forme sera importée en l'état — la même règle que la liste des calques. */
	isEnabled: (shape: PlanShape) => boolean;
	doubtIndex: number;
	truncated: number;
	onGoDoubt: (index: number) => void;
	onArbitrate: (shape: Pick<PlanShape, 'layer' | 'index'>, decision: PlanArbitration) => void;
	onCorrect: (doubt: PlanDoubt, value: string) => void;
	onClose: () => void;
};

/** Marge autour d'une forme quand on cadre dessus, en multiples de sa propre taille. */
const FOCUS_MARGIN = 3;

/** Marge autour de la ZONE D'ANALYSE à l'ouverture, en fraction de sa taille. */
const REGION_MARGIN = 0.04;

/** Voile posé sur le plan HORS zone d'analyse : assez pour l'effacer, pas pour le cacher. */
const OUTSIDE_VEIL = 'rgba(255, 255, 255, 0.72)';

/**
 * **Le liseré de la forme EN COURS d'arbitrage.**
 *
 * La carte de doute dit « le contour n° 47 porte deux numéros » et la vue se cadre dessus — mais
 * une fois cadré, on voit dix formes ambre voisines et rien ne dit LAQUELLE est celle de la
 * carte. Cadrer n'est pas désigner.
 *
 * Le liseré est volontairement HORS de la palette des états (vert, ambre, gris) : il ne dit pas
 * ce que la forme deviendra — sa couleur de remplissage continue de le dire —, il dit « c'est
 * de celle-ci qu'on parle ». D'où un noir tracé en pointillés qui défilent : une facture qu'on
 * ne confond avec aucun état, et qui attire l'œil sans repeindre la forme.
 */
const CURRENT_HALO = '#ffffff';
const CURRENT_OUTLINE = '#141414';

/** Épaisseurs du liseré, en pixels ÉCRAN (converties à la volée, comme celles des formes). */
const CURRENT_HALO_WIDTH = 7;
const CURRENT_OUTLINE_WIDTH = 1.5;

/**
 * **Ce que la modale doit CADRER à l'ouverture.**
 *
 * Le plan est toujours affiché en entier — les contours sont en pixels de CETTE image, et les
 * recadrer serait réécrire une projection pour rien. Mais quand l'analyse a été restreinte à une
 * zone, ouvrir sur le plan entier montre surtout du vide : les formes tiennent dans un coin, trop
 * petites pour qu'on distingue quoi que ce soit, et rien ne dit à l'écran que le reste du plan
 * n'a pas été analysé. On ouvre donc SUR la zone, et le hors-zone part sous un voile.
 */
function openingView(source: PlanSourceResult): PlanView {
	const region = source.analysisRegion;
	if (!region) return { x: 0, y: 0, w: source.width, h: source.height };

	const width = region.maxX - region.minX;
	const height = region.maxY - region.minY;

	return {
		x: region.minX - width * REGION_MARGIN,
		y: region.minY - height * REGION_MARGIN,
		w: width * (1 + 2 * REGION_MARGIN),
		h: height * (1 + 2 * REGION_MARGIN),
	};
}

/**
 * Voile du hors-zone : le plan entier percé du rectangle analysé (`evenodd`).
 *
 * Un simple cadre ne suffirait pas — sur un plan dense, une ligne de plus se perd dans le
 * dessin. Le voile, lui, répond à la question qu'on se pose vraiment devant cet écran : « et
 * ces stands-là, pourquoi ne sont-ils pas retenus ? » Parce qu'ils sont hors zone.
 */
function veilPath(source: PlanSourceResult, region: PlanRegion): string {
	return `M0,0 H${source.width} V${source.height} H0 Z`
		+ ` M${region.minX},${region.minY} H${region.maxX} V${region.maxY} H${region.minX} Z`;
}

/** Les points d'une forme, dans l'attribut `points` d'un `<polygon>`. */
function pointsOf(shape: PlanShape): string {
	return shape.points.map(point => `${point[0]},${point[1]}`).join(' ');
}

/**
 * **La légende, et le compte de chaque état.**
 *
 * Elle existe parce que le code couleur ne se devinait pas : les formes retenues reprenaient la
 * couleur du plan — deux bleus pour un seul état sur un plan bleu —, et les deux gris de
 * « écartée » et « non reconnue » étaient à peine distinguables. Une couleur qui n'est expliquée
 * nulle part n'est pas un code, c'est une décoration.
 *
 * Les pastilles sont peintes avec les styles des formes eux-mêmes ({@link PLAN_SHAPE_STYLES}) :
 * une légende recopiée à la main ment dès la première retouche de palette.
 */
const PlanShapesLegend = ({ counts }: { counts: Record<PlanShapeState, number> }) => (
	<View gap={6}>
		{PLAN_SHAPE_LEGEND.map(({ state, label, hint }) => {
			const style = PLAN_SHAPE_STYLES[state];
			const count = counts[state];

			return (
				<View key={state} row gap={8} style={{ alignItems: 'baseline', opacity: count ? 1 : 0.45 }}>
					<View
						style={{
							width: 16,
							height: 16,
							flex: '0 0 auto',
							borderRadius: 3,
							background: style.bgColor,
							border: `${Math.max(style.borderWidth, 1)}px ${style.borderDashed ? 'dashed' : 'solid'} ${style.borderColor}`,
						}}
					/>

					<View flex>
						<View size={13}>
							<b>{count}</b> — {label}
						</View>
						<View size={11} color="#999">{hint}</View>
					</View>
				</View>
			);
		})}
	</View>
);

const PlanShapesModal = ({
	open, source, shapes, doubts, arbitrations, corrections,
	isEnabled, doubtIndex, truncated, onGoDoubt, onArbitrate, onCorrect, onClose,
}: Props) => {
	const viewport = usePlanViewport(source, () => openingView(source));
	const { view, setView } = viewport;

	const theme = useTheme();
	const doubtful = React.useMemo(() => new Set(doubts.map(doubtKey)), [doubts]);
	const current = doubts[doubtIndex];

	// Repartir du cadrage d'ouverture à chaque fois ({@link openingView}) : rouvrir la modale sur
	// le zoom de la session précédente désoriente plus que ça ne fait gagner.
	React.useEffect(() => {
		if (open) setView(openingView(source));
	}, [open, source]);

	/** Cadre la vue sur une forme, avec assez de voisines autour pour la situer. */
	const focusOn = React.useCallback((shape: PlanShape) => {
		const xs = shape.points.map(point => point[0]);
		const ys = shape.points.map(point => point[1]);
		const width = Math.max(...xs) - Math.min(...xs);
		const height = Math.max(...ys) - Math.min(...ys);
		// Une forme dégénérée (trait) donnerait une fenêtre nulle : on garde un plancher.
		const size = Math.max(width, height, source.width / 100) * (1 + 2 * FOCUS_MARGIN);

		setView({
			x: shape.centroid[0] - size / 2,
			y: shape.centroid[1] - size / 2,
			w: size,
			h: size,
		});
	}, [source.width]);

	// La carte de doute et la vue se suivent : lire « le contour n° 47 porte deux numéros »
	// sans voir lequel ne permet de rien décider.
	React.useEffect(() => {
		if (!open || !current) return;

		const shape = shapes.find(entry => entry.layer === current.layer && entry.index === current.index);
		if (shape) focusOn(shape);
	}, [open, current, shapes, focusOn]);

	// Chaque forme avec l'état qu'elle DOIT afficher, calculé une fois : la légende compte
	// exactement ce que le plan montre, et ne peut donc pas le contredire.
	const entries = shapes.map(shape => {
		const key = doubtKey(shape);
		const enabled = isEnabled(shape);
		const state = planShapeState({
			shape,
			enabled,
			// Un doute déjà tranché n'est plus un doute : il repasse en vert ou en gris, sinon
			// la file se viderait sans que le plan change, et on ne saurait plus où on en est.
			doubtful: doubtful.has(key) && undefined === arbitrations[key],
		});

		return { shape, key, enabled, state, style: PLAN_SHAPE_STYLES[state] };
	});

	// La forme de la carte de doute OUVERTE — celle que la vue vient de cadrer, et la seule dont
	// on demande quelque chose à cet instant.
	const highlighted = current ? entries.find(entry => entry.key === doubtKey(current)) : undefined;

	const counts = entries.reduce<Record<PlanShapeState, number>>(
		(total, entry) => ({ ...total, [entry.state]: total[entry.state] + 1 }),
		{ enabled: 0, doubtful: 0, unsupported: 0, discarded: 0 },
	);

	// ⚠️ **`<Modal open>` ne se pilote PAS après le montage.** `ModalStore` lit `props.open`
	// dans son CONSTRUCTEUR, et le store est créé une fois pour toutes (`useWillMount`) : passer
	// `open` de `false` à `true` ne rouvre rien, la modale reste invisible sans la moindre
	// erreur. C'est ce qui s'est produit à la première version de cet écran.
	//
	// On ne monte donc la modale QUE lorsqu'elle doit être ouverte : son store naît alors avec
	// `open: true`. Le retour anticipé est placé APRÈS tous les hooks, qui doivent rester
	// appelés dans le même ordre à chaque rendu.
	if (!open) return null;

	return (
		<Modal
			open
			onClose={onClose}
			fullScreen
			title="Formes à importer"
			footer={<Button type="primary" onClick={onClose}>Terminer le tri</Button>}
			bodyPadding={0}
		>
			{/* La feuille de style de `fullScreen` donne au corps `height: 100 %` du contenu,
			    c'est-à-dire toute la fenêtre — en-tête et pied compris. Sans cette hauteur
			    explicite, la colonne de droite passe sous le pied de page. */}
			<View row style={{ height: 'calc(100vh - 170px)', minHeight: 360 }}>
				{/* Fond BLANC, et pas le fond du thème : le plan est un document sur papier, et
				    le rendu vectoriel d'un DXF est transparent à traits noirs — sur un fond
				    sombre il disparaîtrait purement et simplement. */}
				<View flex style={{ position: 'relative', background: '#fff', overflow: 'hidden' }}>
					<svg
						ref={viewport.svgRef}
						viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
						style={{ width: '100%', height: '100%', cursor: viewport.panning ? 'grabbing' : 'grab', touchAction: 'none' }}
						onWheel={viewport.onWheel}
						onPointerDown={viewport.startPan}
						onPointerMove={viewport.movePan}
						onPointerUp={viewport.endPan}
						onPointerLeave={viewport.endPan}
					>
						{/* Le plan, à l'échelle 1 de son propre repère : les contours reçus sont
						    dans CES pixels-là, rien à projeter. */}
						<image href={source.imageDataUri} x={0} y={0} width={source.width} height={source.height} />

						{/* Hors zone d'analyse : ce que le serveur n'a pas regardé ne doit pas se
						    lire comme un plan qu'on aurait oublié de trier. */}
						{source.analysisRegion && (
							<>
								<path d={veilPath(source, source.analysisRegion)} fillRule="evenodd" fill={OUTSIDE_VEIL} />
								<rect
									x={source.analysisRegion.minX}
									y={source.analysisRegion.minY}
									width={source.analysisRegion.maxX - source.analysisRegion.minX}
									height={source.analysisRegion.maxY - source.analysisRegion.minY}
									fill="none"
									stroke="#1668dc"
									strokeWidth={(1.5 * view.w) / source.width}
									strokeDasharray={`${(6 * view.w) / source.width},${(4 * view.w) / source.width}`}
								/>
							</>
						)}

						{entries.map(({ shape, key, enabled, state, style }) => {
							// Le trait doit garder son épaisseur À L'ÉCRAN quel que soit le zoom :
							// sans ça un contour disparaît dès qu'on dézoome.
							const stroke = (style.borderWidth * view.w) / source.width;
							const legend = PLAN_SHAPE_LEGEND.find(entry => entry.state === state);

							return (
								<polygon
									key={key}
									points={pointsOf(shape)}
									fill={style.bgColor}
									stroke={style.borderColor}
									strokeWidth={stroke}
									strokeDasharray={style.borderDashed ? `${stroke * 3},${stroke * 2}` : undefined}
									style={{ cursor: 'pointer' }}
									onPointerDown={event => event.stopPropagation()}
									onClick={() => onArbitrate(shape, enabled ? 'discarded' : 'kept')}
								>
									<title>
										{`${shape.layer} — forme ${shape.index}`}
										{`\n${legend?.label ?? ''} : ${legend?.hint ?? ''}`}
										{'\nCliquer pour changer'}
									</title>
								</polygon>
							);
						})}

						{/* Dessiné APRÈS toutes les formes, donc AU-DESSUS : une voisine qui
						    passerait par-dessus le liseré ferait exactement perdre ce qu'il
						    sert à gagner. `pointer-events: none` pour que le clic continue
						    d'atteindre la forme elle-même. */}
						{highlighted && (
							<g data-current="true" pointerEvents="none">
								{/* Halo blanc : sur un plan noir et dense, un trait noir de plus
								    se perd dans le dessin. */}
								<polygon
									points={pointsOf(highlighted.shape)}
									fill="none"
									stroke={CURRENT_HALO}
									strokeWidth={(CURRENT_HALO_WIDTH * view.w) / source.width}
									strokeOpacity={0.9}
								/>
								{/* La couleur d'ÉTAT, redessinée par-dessus le halo : le liseré
								    désigne la forme, il ne doit pas cacher ce qu'elle deviendra. */}
								<polygon
									points={pointsOf(highlighted.shape)}
									fill="none"
									stroke={highlighted.style.borderColor}
									strokeWidth={(highlighted.style.borderWidth * view.w) / source.width}
								/>
								<polygon
									points={pointsOf(highlighted.shape)}
									fill="none"
									stroke={CURRENT_OUTLINE}
									strokeWidth={(CURRENT_OUTLINE_WIDTH * view.w) / source.width}
									strokeDasharray={`${(8 * view.w) / source.width},${(5 * view.w) / source.width}`}
								>
									{/* Pointillés qui défilent : le seul mouvement de l'écran,
									    donc le seul endroit où l'œil va. SMIL plutôt qu'une
									    animation CSS — pas de feuille de style à injecter dans
									    une modale, et l'animation suit le zoom sans recalcul. */}
									<animate
										attributeName="stroke-dashoffset"
										from={(13 * view.w) / source.width}
										to="0"
										dur="0.8s"
										repeatCount="indefinite"
									/>
								</polygon>
							</g>
						)}
					</svg>

					<PlanZoomControls
						zoomPercent={viewport.zoomPercent}
						onZoomIn={viewport.zoomIn}
						onZoomOut={viewport.zoomOut}
						onFit={viewport.fit}
					/>

					<View style={{ position: 'absolute', left: 12, bottom: 12, background: 'rgba(255,255,255,.9)', padding: '6px 10px', borderRadius: 4, border: '1px solid #eee' }}>
						<View size={11} color="#666">
							Molette : zoom · glisser : déplacer · clic sur une forme : la garder ou l’écarter
							{source.analysisRegion ? ' · le plan grisé est hors zone d’analyse' : ''}
						</View>
					</View>
				</View>

				<View
					gap={10}
					style={{
						width: 380,
						borderLeft: `1px solid ${theme.colorBorderSecondary}`,
						padding: 12,
						overflowY: 'auto',
					}}
				>
					<PlanShapesLegend counts={counts} />

					{/* La légende dit les états ; ceci dit la SÉLECTION, qui n'en est pas un. */}
					{highlighted && (
						<View row gap={8} style={{ alignItems: 'center' }}>
							<View
								style={{
									width: 16,
									height: 16,
									flex: '0 0 auto',
									borderRadius: 3,
									border: `2px dashed ${CURRENT_OUTLINE}`,
									boxShadow: `0 0 0 2px ${CURRENT_HALO}`,
								}}
							/>
							<View size={11} color="#999">
								Le contour noir animé sur le plan est la forme de la carte ci-dessous.
							</View>
						</View>
					)}

					{0 < truncated && (
						<Alert
							type="warning"
							showIcon
							message={`${truncated} forme(s) non transmises`}
							description="Le plan est trop dense pour tout afficher : restreignez la zone d’analyse ou les calques pour voir le reste."
						/>
					)}

					<PlanDoubtQueue
						doubts={doubts}
						arbitrations={arbitrations}
						corrections={corrections}
						current={doubtIndex}
						onGo={onGoDoubt}
						onArbitrate={(doubt, decision) => onArbitrate(doubt, decision)}
						onCorrect={onCorrect}
					/>
				</View>
			</View>
		</Modal>
	);
};

export default PlanShapesModal;
